import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../lib/useApi'
import { useAuth } from '../../lib/auth.jsx'
import { THEMES, THEME_IDS } from '../Themes/themes.js'
import { applyTheme } from '../../lib/useTheme.js'
import { WakeModal, SmartphoneModal, EveningModal, BedtimeModal } from '../Health/modals.jsx'

export default function HeaderBar({ profile, onOpenDb }) {
  const [openModal, setOpenModal] = useState(null)   // 'wake' | 'phone' | 'evening' | 'bed'
  const hour = new Date().getHours()
  const sug = hour < 11 ? 'wake' : hour < 17 ? 'phone' : hour < 22 ? 'evening' : 'bed'
  const { signOut, user } = useAuth()
  const api = useApi()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function setTheme(id) {
    applyTheme(id)
    qc.setQueryData(['profile'], (old) => ({ ...(old || {}), theme: id }))
    try {
      await api.patch('/profile', { theme: id })
      qc.invalidateQueries({ queryKey: ['profile'] })
    } catch { /* dev-no-DB → optimistic stays */ }
  }

  return (
    <header className="px-4 py-3 flex items-center justify-between border-b border-card sticky top-0 backdrop-blur z-20"
            style={{ background: 'color-mix(in srgb, var(--bg) 85%, transparent)' }}>
      <div className="flex items-center gap-2">
        <img src="/icon-192.png" alt="" className="w-6 h-6 rounded-themed" />
        <span className="font-display tracking-[0.25em] text-sm text-accent">PROJECT ARISE</span>
      </div>

      <div className="flex items-center gap-2" ref={wrapRef}>
        <button onClick={() => setOpenModal(sug)}
                title={`Quick: ${sug}`}
                className="label-mini hover:text-accent px-2 py-1 hidden sm:inline-flex">
          {sug === 'wake'    ? '☀ Wake'  :
           sug === 'phone'   ? '📱 Phone' :
           sug === 'evening' ? '🌙 Abend' :
                               '😴 Bed'}
        </button>
        {onOpenDb && (
          <button onClick={onOpenDb} className="hidden md:inline-flex label-mini hover:text-accent px-2 py-1">
            DB
          </button>
        )}

        <div className="relative">
          <button onClick={() => setOpen((x) => !x)}
                  className="px-3 py-1.5 rounded-themed border border-card text-sm tap">
            {profile?.display_name || user?.email?.split('@')[0] || '…'}
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-2 w-60 border border-card bg-card rounded-themed p-3 space-y-3 z-30 animate-system-open shadow-themed"
                 style={{ background: 'var(--bg)' }}>
              <div className="text-xs opacity-70">{user?.email}</div>

              <div>
                <div className="label-mini mb-1">Theme</div>
                <div className="grid grid-cols-2 gap-1">
                  {THEME_IDS.map((id) => (
                    <button key={id}
                            onClick={() => setTheme(id)}
                            onMouseEnter={() => applyTheme(id)}
                            onMouseLeave={() => applyTheme(profile?.theme || 'hunter')}
                            className={`text-xs px-2 py-1 rounded-themed border ${profile?.theme === id ? 'border-[var(--accent)] text-accent' : 'border-card'}`}>
                      {THEMES[id].label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={signOut} className="w-full text-left text-sm hover:text-accent">Logout</button>
            </div>
          )}
        </div>
      </div>

      <WakeModal       open={openModal === 'wake'}    onClose={() => setOpenModal(null)} />
      <SmartphoneModal open={openModal === 'phone'}   onClose={() => setOpenModal(null)} />
      <EveningModal    open={openModal === 'evening'} onClose={() => setOpenModal(null)} />
      <BedtimeModal    open={openModal === 'bed'}     onClose={() => setOpenModal(null)} />
    </header>
  )
}
